import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, ArrowRight } from "lucide-react";
import { IMAGES } from "../data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] } }),
};

const headLines = ["Transforming Organisations.", "Empowering People.", "Creating Sustainable Impact."];

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.12]);
  const go = (h) => document.querySelector(h)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" className="relative min-h-screen overflow-hidden bg-emerald-darkest" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="AI-powered business transformation" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-darkest via-emerald-darkest/85 to-emerald-darkest/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-darkest via-transparent to-emerald-darkest/40" />
      </motion.div>

      <div className="grain absolute inset-0 z-[2]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-5 pt-32 pb-20 sm:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-gold" />
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-gold-light">Strategise • Optimise • Transform</span>
        </motion.div>

        <h1 className="max-w-5xl font-serif text-[2.6rem] font-light leading-[1.05] tracking-tight text-off-white sm:text-6xl lg:text-7xl">
          {headLines.map((t, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 2 ? <span className="text-gradient-gold">{t}</span> : t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-off-white/75 sm:text-lg">
          OnwardTranscends partners with organisations and professionals to improve performance, optimise operations,
          strengthen supply chains, embrace digital transformation and unlock sustainable growth — turning complex
          challenges into measurable results.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row">
          <button data-testid="hero-book-btn" onClick={() => go("#contact")}
            className="group flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold text-emerald-darkest shadow-[0_10px_30px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:-translate-y-1">
            <CalendarCheck size={18} /> Book a Consultation
          </button>
          <button data-testid="hero-services-btn" onClick={() => go("#services")}
            className="group flex items-center justify-center gap-2 rounded-full border border-off-white/25 px-7 py-4 text-sm font-semibold text-off-white transition-colors duration-300 hover:border-gold hover:text-gold">
            Explore Our Services <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 grid max-w-3xl grid-cols-2 gap-6 border-t border-white/10 pt-8 sm:grid-cols-4">
          {[["Strategy", "Operational"], ["Supply Chain", "Resilient"], ["AI & Digital", "Intelligent"], ["People", "High-performing"]].map(([a, b]) => (
            <div key={a}>
              <p className="text-xs uppercase tracking-widest text-gold-light">{b}</p>
              <p className="mt-1 font-serif text-xl text-off-white">{a}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
